import { XMarkIcon } from "@heroicons/react/20/solid";
import { DownloadIcon } from "lucide-react";
import { TextPreview } from "./TextPreview";
import { isTextFile } from "./Attachments";
import { useEffect } from "react";

export interface PreviewFileProps {
  previewFile: {
    url: string;
    type: string;
    contentType: string;
    name: string;
  };
  closeFilePreview: () => void;
}

function PreviewFile({ previewFile, closeFilePreview }: PreviewFileProps) {
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeFilePreview();
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [closeFilePreview]);
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      onClick={closeFilePreview}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative max-w-4xl max-h-screen p-4 w-full">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-800 truncate">
              {previewFile.name}
            </h3>
            <div className="flex items-center flex-row gap-4">
              <a
                href={previewFile.url}
                download={previewFile.name}
                className="text-gray-500 hover:text-green-500 mx-2"
                onClick={(e) => e.stopPropagation()}
                aria-label="Download file"
                target="_blank"
                rel="noopener noreferrer"
              >
                <DownloadIcon className="h-5 w-5" />
              </a>
              <button
                onClick={closeFilePreview}
                className="text-gray-500 hover:text-red-500"
                aria-label="Close preview"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="bg-gray-50" onClick={(e) => e.stopPropagation()}>
            {previewFile.contentType.startsWith("image/") ? (
              <img
                src={previewFile.url}
                alt={previewFile.name}
                className="max-w-full max-h-[70vh] object-contain mx-auto p-4"
              />
            ) : previewFile.contentType === "application/pdf" ? (
              <iframe
                src={`${previewFile.url}#toolbar=1&navpanes=0`}
                className="w-full h-[70vh]"
                title={`PDF Preview: ${previewFile.name}`}
              />
            ) : isTextFile(previewFile.contentType) ? (
              <div className="bg-white p-4 rounded border border-gray-300 h-[70vh] overflow-auto">
                <TextPreview
                  url={previewFile.url}
                  type={previewFile.contentType}
                />
              </div>
            ) : (
              <div className="text-center p-12">
                <p className="text-gray-600 mb-4">
                  This file type can't be previewed directly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreviewFile;
